import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ChevronLeft, ChevronRight,
  AlertOctagon, AlertTriangle, Inbox, CheckCircle2, XCircle,
  Eye, EyeOff, ShieldX, X,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminAds.css';

const STATUS_CHIPS = [
  { id: '',            color: 'blue',   en: 'All',         ar: 'الكل' },
  { id: 'pending',     color: 'yellow', en: 'Pending',     ar: 'قيد المعالجة' },
  { id: 'reviewed',    color: 'blue',   en: 'Reviewed',    ar: 'تمت المراجعة' },
  { id: 'validated',   color: 'red',    en: 'Valid',       ar: 'صحيحة' },
  { id: 'invalidated', color: 'gray',   en: 'Invalid',     ar: 'غير صحيحة' },
  { id: 'dismissed',   color: 'gray',   en: 'Dismissed',   ar: 'مرفوضة' },
];

// All canonical actions the admin can perform on a single report.
const ACTIONS = [
  {
    id: 'review',
    en: 'Review',
    ar: 'مراجعة',
    confirm_en: 'Mark this report as reviewed. No verdict applied yet.',
    confirm_ar: 'وسم هذا البلاغ كمُراجَع دون اتخاذ قرار نهائي.',
    Icon: Eye,
    color: 'blue',
    requires_reason: false,
  },
  {
    id: 'mark_valid',
    en: 'Mark Valid',
    ar: 'تمييز كصحيح',
    confirm_en: 'Mark this report as VALID. The ad owner will receive a trust penalty + a posting strike.',
    confirm_ar: 'وسم هذا البلاغ كـ "صحيح". سيتم خفض ثقة صاحب الإعلان وإضافة عقوبة نشر.',
    Icon: CheckCircle2,
    color: 'red',
    requires_reason: false,
    danger: true,
  },
  {
    id: 'mark_invalid',
    en: 'Mark Invalid',
    ar: 'تمييز كغير صحيح',
    confirm_en: 'Mark this report as INVALID. No penalty applied to the ad owner.',
    confirm_ar: 'وسم هذا البلاغ كـ "غير صحيح". لن يتم تطبيق أي عقوبة على صاحب الإعلان.',
    Icon: XCircle,
    color: 'gray',
    requires_reason: false,
  },
  {
    id: 'dismiss',
    en: 'Dismiss',
    ar: 'تجاهل',
    confirm_en: 'Dismiss this report. No verdict, no penalty.',
    confirm_ar: 'تجاهل هذا البلاغ. بدون قرار وبدون عقوبة.',
    Icon: X,
    color: 'gray',
    requires_reason: false,
  },
  {
    id: 'hide_ad',
    en: 'Hide Ad',
    ar: 'إخفاء الإعلان',
    confirm_en: 'Hide the reported ad publicly AND mark this report as valid. The ad will disappear from Shop/Home but remain visible to admins.',
    confirm_ar: 'إخفاء الإعلان من Shop والصفحة الرئيسية، مع تمييز البلاغ كصحيح. الإعلان سيبقى مرئياً للمشرفين فقط.',
    Icon: EyeOff,
    color: 'red',
    requires_reason: true,
    danger: true,
  },
];

const PAGE_SIZE = 25;

function ConfirmModal({ open, action, report, isArabic, onCancel, onConfirm, working }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open, action?.id]);
  if (!open || !action) return null;
  const reasonRequired = action.requires_reason;
  const canSubmit = !working && (!reasonRequired || reason.trim().length > 0);
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="admin-reports-confirm-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 12,
      }}
    >
      <div
        className="admin-card"
        style={{ width: '100%', maxWidth: 460, padding: 18, background: '#fff', color: '#0f172a', boxShadow: '0 16px 40px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <action.Icon size={18} color={action.danger ? '#dc2626' : '#0f172a'} />
          <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a', fontWeight: 700 }}>
            {isArabic ? action.ar : action.en}
          </h3>
        </div>
        <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.55, marginTop: 4 }}>
          {isArabic ? action.confirm_ar : action.confirm_en}
        </p>
        {report?.ad?.title && (
          <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#0f172a' }}>
            <div style={{ fontWeight: 600 }}>{report.ad.title}</div>
            <div style={{ color: '#64748b' }}>{report.ad_id}</div>
          </div>
        )}

        <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 600, color: '#0f172a' }} htmlFor="report-action-reason">
          {reasonRequired
            ? (isArabic ? 'السبب (مطلوب — يُسجَّل في سجل المراجعة):' : 'Reason (required — saved to audit log):')
            : (isArabic ? 'ملاحظة (اختيارية — تُسجَّل في سجل المراجعة):' : 'Note (optional — saved to audit log):')}
        </label>
        <textarea
          id="report-action-reason"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isArabic ? 'اكتب السبب…' : 'Write the reason…'}
          data-testid="admin-reports-confirm-reason"
          className="admin-input"
          style={{ width: '100%', resize: 'vertical', color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: 8, fontSize: 13 }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            className="admin-btn"
            onClick={onCancel}
            disabled={working}
            data-testid="admin-reports-confirm-cancel"
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            type="button"
            className={`admin-btn ${action.danger ? 'danger' : ''}`}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            data-testid="admin-reports-confirm-submit"
            style={action.danger ? { background: '#dc2626', color: '#fff', fontWeight: 700 } : {}}
          >
            {working
              ? <><Loader2 className="animate-spin" size={14} /> {isArabic ? 'جارٍ التنفيذ…' : 'Working…'}</>
              : (isArabic ? 'تأكيد' : 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReports() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';

  const [statusFilter, setStatusFilter] = useState('pending');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ reports: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // { report, action }
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/reports',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const apiBase = getApiBase();
      const path = `/api/admin/reports?${params.toString()}`;
      const resp = await fetch(`${apiBase}${path}`, {
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        setError({
          endpoint: '/api/admin/reports',
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
        });
        return;
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError({
        endpoint: '/api/admin/reports',
        status: 0,
        message: (e && e.message) ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  const requestAction = (report, action) => {
    setPendingAction({ report, action });
  };

  const submitAction = async (reason) => {
    if (!pendingAction) return;
    const { report, action } = pendingAction;
    setWorking(true);
    try {
      const r = await fetch(`${getApiBase()}/api/admin/reports/action`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_id: report.report_id,
          action: action.id,
          note: reason || '',
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        return;
      }
      if (j.already_applied) {
        toast.info(isArabic ? 'الإجراء مطبَّق مسبقاً' : 'Action already applied');
      } else {
        toast.success(isArabic ? 'تم الإجراء وسُجِّل في سجل المراجعة' : 'Action recorded in audit log');
      }
      setPendingAction(null);
      await load();
    } catch (_e) {
      toast.error(isArabic ? 'حدث خطأ في الشبكة' : 'Network error');
    } finally {
      setWorking(false);
    }
  };

  const renderActions = (r) => {
    const status = r.status || 'pending';
    // After a terminal verdict, disable the verdict buttons but keep "Hide Ad" available
    // so admins can still take down a confirmed-valid scam at any time.
    const isTerminal = ['validated', 'invalidated', 'dismissed'].includes(status);
    return (
      <div className="admin-ads-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
        {ACTIONS.map((a) => {
          const isCurrent =
            (a.id === 'mark_valid' && status === 'validated') ||
            (a.id === 'mark_invalid' && status === 'invalidated') ||
            (a.id === 'dismiss' && status === 'dismissed') ||
            (a.id === 'review' && status === 'reviewed');
          const disabled =
            isCurrent ||
            (a.id !== 'hide_ad' && a.id !== 'review' && isTerminal);
          return (
            <button
              key={a.id}
              type="button"
              className={`admin-btn ${a.danger ? 'danger' : ''}`}
              onClick={() => requestAction(r, a)}
              disabled={disabled}
              data-testid={`admin-reports-${a.id}-${r.report_id}`}
              title={isArabic ? a.ar : a.en}
              style={{ padding: '4px 8px', fontSize: 11 }}
            >
              <a.Icon size={12} />
              <span style={{ marginInlineStart: 4 }}>{isArabic ? a.ar : a.en}</span>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="admin-reports">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-reports-title">
            <AlertTriangle size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'البلاغات' : 'Reports'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'بلاغات الإعلانات من المستخدمين. كل إجراء يتطلب تأكيداً ويُسجَّل في سجل المراجعة.'
              : 'User-submitted ad reports. Every action requires confirmation and is recorded in the audit log.'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-reports-refresh">
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-ads-filters">
        <div className="admin-ads-status-filters" role="tablist">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.id || 'all'}
              type="button"
              className={`admin-pill ${statusFilter === c.id ? c.color : 'gray'}`}
              onClick={() => { setStatusFilter(c.id); setSkip(0); }}
              data-testid={`admin-reports-filter-${c.id || 'all'}`}
              data-active={statusFilter === c.id ? 'true' : 'false'}
              aria-pressed={statusFilter === c.id}
              role="tab"
            >
              {isArabic ? c.ar : c.en}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div
            className="admin-card"
            data-testid="admin-reports-error"
            style={{
              margin: 12,
              borderInlineStart: '3px solid #ef4444',
              background: 'rgba(239, 68, 68, 0.06)',
            }}
          >
            <h4 style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <AlertOctagon size={14} />
              {isArabic ? 'تعذر تحميل البلاغات' : 'Failed to load reports'}
            </h4>
            <ul style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, paddingInlineStart: 18 }}>
              <li>{isArabic ? 'النقطة' : 'Endpoint'}: <code>{error.endpoint}</code></li>
              <li>{isArabic ? 'حالة HTTP' : 'HTTP status'}: <strong>{error.status}</strong></li>
              <li>{isArabic ? 'التفاصيل' : 'Detail'}: <span>{error.message}</span></li>
            </ul>
            <button type="button" className="admin-btn" onClick={load} style={{ marginTop: 6 }} data-testid="admin-reports-error-retry">
              <RefreshCw size={14} /> {isArabic ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}

        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-reports-table">
            <thead>
              <tr>
                <th>{isArabic ? 'الإعلان' : 'Ad'}</th>
                <th>{isArabic ? 'السبب' : 'Reason'}</th>
                <th>{isArabic ? 'المُبلِغ' : 'Reporter'}</th>
                <th>{isArabic ? 'الحالة' : 'Status'}</th>
                <th>{isArabic ? 'التاريخ' : 'Date'}</th>
                <th style={{ textAlign: 'end' }}>{isArabic ? 'إجراءات' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center' }}>
                  <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
                </td></tr>
              )}
              {!loading && (data.reports || []).length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-reports-empty">
                  <Inbox size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
                  <div>{isArabic ? 'لا توجد بلاغات بهذه الحالة.' : 'No reports match this status.'}</div>
                </td></tr>
              )}
              {!loading && (data.reports || []).map((r) => (
                <tr key={r.report_id} data-testid={`admin-reports-row-${r.report_id}`}>
                  <td>
                    <div className="admin-ads-ad-meta">
                      <div className="admin-ads-ad-title">{r.ad?.title || '—'}</div>
                      <div className="admin-ads-ad-id">{r.ad_id}</div>
                      {r.ad?.status && (
                        <div style={{ marginTop: 2 }}>
                          <span className={`admin-pill ${r.ad.status === 'removed_by_moderation' ? 'red' : 'gray'}`} style={{ fontSize: 10 }}>
                            {r.ad.status}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <code style={{ fontSize: 12 }}>{r.reason || r.reason_code || '—'}</code>
                    {r.note && <div style={{ fontSize: 11, color: 'var(--admin-text-dim)', marginTop: 2 }}>{r.note}</div>}
                    {r.admin_note && (
                      <div style={{ fontSize: 11, color: '#0ea5e9', marginTop: 2, fontStyle: 'italic' }}>
                        {isArabic ? 'ملاحظة المشرف: ' : 'Admin note: '}{r.admin_note}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="admin-ads-owner">
                      <div>{r.reporter?.name || '—'}</div>
                      <div className="admin-ads-owner-email">{r.reporter?.email || ''}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-pill ${
                      r.status === 'validated' ? 'red'
                        : r.status === 'invalidated' ? 'gray'
                        : r.status === 'dismissed' ? 'gray'
                        : r.status === 'reviewed' ? 'blue'
                        : 'yellow'
                    }`} data-testid={`admin-reports-status-${r.report_id}`}>
                      {r.status || 'pending'}
                    </span>
                    {r.reviewed_by_admin_email && (
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                        {isArabic ? 'بواسطة:' : 'by:'} {r.reviewed_by_admin_email}
                      </div>
                    )}
                  </td>
                  <td className="admin-ads-date">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>{renderActions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-reports-total">
            {isArabic ? `الإجمالي: ${data.total}` : `Total: ${data.total}`}
          </span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="admin-reports-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label" data-testid="admin-reports-page-label">
              {currentPage} / {pages}
            </span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="admin-reports-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={!!pendingAction}
        action={pendingAction?.action}
        report={pendingAction?.report}
        isArabic={isArabic}
        working={working}
        onCancel={() => !working && setPendingAction(null)}
        onConfirm={submitAction}
      />

      {/* keep ShieldX import referenced to avoid linter warnings if removed */}
      <span style={{ display: 'none' }}><ShieldX size={1} /></span>
    </div>
  );
}
